import { useSearchParams } from 'react-router';
import { useCallback, useMemo } from 'react';
import { useSelectionStore } from '@/stores/selectionStore';

const ENTITY_PARAMS = ['ticketId', 'projectId', 'crewId', 'entityId'] as const;

export const useDialogManager = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const clearSelection = useSelectionStore((s) => s.clear);

  const dialogType = searchParams.get('dialog');

  // ── Individual param accessors ────────────────────────────────────────────
  const ticketId = searchParams.get('ticketId') ?? null;
  const projectId = searchParams.get('projectId') ?? null;
  const crewId = searchParams.get('crewId') ?? null;

  // ── Generic entityId — first non-null param (backwards compat) ────────────
  const entityId = useMemo(
    () => ticketId ?? projectId ?? crewId ?? null,
    [ticketId, projectId, crewId],
  );

  const openDialog = useCallback(
    (type: string, id?: string) => {
      setSearchParams(
        (prev) => {
          const p = new URLSearchParams(prev);

          // ── Clean slate: remove all dialog-related params ─────────
          p.delete('dialog');
          ENTITY_PARAMS.forEach((key) => p.delete(key));

          // ── Set the new dialog ────────────────────────────────────
          p.set('dialog', type);

          if (id) {
            if (type === 'create-ticket') p.set('projectId', id);
            else if (type.includes('ticket')) p.set('ticketId', id);
            else if (type.includes('project')) p.set('projectId', id);
            else if (type.includes('crew')) p.set('crewId', id);
            else p.set('entityId', id);
          }

          return p;
        },
        { replace: true },
      );
    },
    [setSearchParams],
  );

  const closeDialog = useCallback(() => {
    clearSelection();
    setSearchParams(
      (prev) => {
        const p = new URLSearchParams(prev);
        p.delete('dialog');
        ENTITY_PARAMS.forEach((key) => p.delete(key));
        return p;
      },
      { replace: true },
    );
  }, [setSearchParams, clearSelection]);

  return {
    dialogType,
    entityId,
    ticketId,
    projectId,
    crewId,
    openDialog,
    closeDialog,
  };
};
